import { ConcessionCalculatorGoogleAnalytics4, pagePath } from './analytics';

function initRadioGroupAccessibility(container) {
  const radios = Array.from(container.querySelectorAll('input[type="radio"]'));
  if (!radios.length) return;

  // Make first/checked tabbable
  function syncTabIndex() {
    const checked = radios.find((r) => r.checked);
    const active = checked || radios[0];
    // eslint-disable-next-line no-return-assign, no-param-reassign
    radios.forEach((r) => r.tabIndex = (r === active ? 0 : -1));
  }

  // When selection changes via mouse/tap, keep tabindex aligned
  radios.forEach((radio) => {
    radio.addEventListener('change', () => syncTabIndex());
  });

  syncTabIndex();

  container.addEventListener('keydown', (e) => {
    const target = e.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.type !== 'radio') return;

    const key = e.key;
    const isArrow = key === 'ArrowDown' || key === 'ArrowUp' || key === 'ArrowLeft' || key === 'ArrowRight';
    const isHomeEnd = key === 'Home' || key === 'End';
    const isSelect = key === ' ' || key === 'Spacebar' || key === 'Enter';

    if (!(isArrow || isHomeEnd || isSelect)) return;

    const currentIndex = radios.indexOf(target);
    if (currentIndex === -1) return;

    // Select on Space/Enter
    if (isSelect) {
      e.preventDefault();
      if (!target.checked) {
        target.checked = true;
        target.dispatchEvent(new Event('change', {
          bubbles: true,
        }));
      }
      syncTabIndex();
      return;
    }

    // Prevent default selecting behaviour
    e.preventDefault();
    let nextIndex = currentIndex;
    if (isHomeEnd) {
      nextIndex = (key === 'Home') ? 0 : radios.length - 1;
    } else {
      const dir = (key === 'ArrowDown' || key === 'ArrowRight') ? 1 : -1;
      nextIndex = (currentIndex + dir + radios.length) % radios.length;
    }

    const next = radios[nextIndex];
    // eslint-disable-next-line no-return-assign, no-param-reassign
    radios.forEach((r) => r.tabIndex = -1);
    next.tabIndex = 0;
    next.focus({
      preventScroll: true,
    });
  });
}

function updateIconCardHeading() {
  const selector = 'h3.at-icon-card__title';
  document.querySelectorAll(selector).forEach((h3) => {
    const h2 = document.createElement('h2');
    // eslint-disable-next-line no-restricted-syntax
    for (const { name, value } of h3.attributes) h2.setAttribute(name, value);
    h2.replaceChildren(...h3.childNodes);
    h3.replaceWith(h2);
  });
}

function getValuesFromFormData(form) {
  const formData = new FormData(form);
  const getLast = (key) => {
    const values = formData.getAll(key);
    return values.length ? values[values.length - 1] : undefined;
  };

  const hasValue = (key, expected) => formData.getAll(key).includes(expected);

  const cards = [];
  if (hasValue('HasCommunityServicesCard', 'true')) {
    cards.push('Community Services Card');
  }

  if (hasValue('HasTotalMobilityCard', 'true')) {
    cards.push('Total Mobility Card');
  }

  if (hasValue('HasBlindLowVisionNzCard', 'true')) {
    cards.push('Blind Low Vision NZ Card');
  }

  return {
    ageRange: getLast('AgeRange'),
    studentType: getLast('StudentStatus'),
    serviceCardType: cards.join(', '),
  };
}

function getConcessionTypesFromResults(results) {
  let resultTypes = [];
  if (results) {
    resultTypes = results.map((c) => c.Type);
  }

  return { concessionResults: resultTypes.length, concessionTypes: resultTypes.join(', ') };
}

function bindAnalyticsToLinkClick(ga, concessionTypes) {
  const links = document.querySelectorAll('.at-icon-card__link a') ?? [];

  links.forEach((link) => {
    link.addEventListener('click', () => {
      const text = (link.textContent || '').trim();
      const href = link.getAttribute('href') || '';
      ga.event('learn_more_clicked', { ...concessionTypes, linkText: text, linkLocation: href }, pagePath.results);
    });
  });
}

export default function initConcessionCalculatorForm(pageTitle, resultCards) {
  const ga4 = new ConcessionCalculatorGoogleAnalytics4(pageTitle);
  let resultTypes = {};
  if (resultCards) {
    resultTypes = getConcessionTypesFromResults(resultCards);
  }

  document.addEventListener('DOMContentLoaded', () => {
    const results = document.getElementById('concession-calculator-results');
    const noResults = document.getElementById('concession-calculator-no-results');
    const form = document.getElementById('concession-calculator-form');
    const ageFieldset = document.getElementById('concession-calculator-age');
    const studentFieldset = document.getElementById('concession-calculator-student');
    const serviceCardFieldset = document.getElementById('concession-calculator-service-card');
    const submitButton = document.getElementById('concession-calculator-submit');
    const submitSpinner = document.getElementById('concession-calculator-save-spinner');
    const submitText = document.getElementById('concession-calculator-submit-text');
    const applyButton = document.getElementById('concession-calculator-apply');

    // Update IconCard
    if (results) {
      updateIconCardHeading();
    }

    // Accesibility
    if (ageFieldset && studentFieldset) {
      initRadioGroupAccessibility(ageFieldset);
      initRadioGroupAccessibility(studentFieldset);
    }

    // Analytics
    if (results || noResults) {
      ga4.event('eligibility_results_viewed', resultTypes, results ? pagePath.results : pagePath.noResults);
    }

    if (applyButton) {
      applyButton.addEventListener('click', () => ga4.event('apply_for_a_concession_clicked', {}, pagePath.results));
    }

    if (form) {
      ga4.pageView(pagePath.form);
    }

    if (results) {
      ga4.pageView(pagePath.results);
      bindAnalyticsToLinkClick(ga4, resultTypes);
    }

    if (noResults) {
      ga4.pageView(pagePath.noResults);
    }

    if (form) {
      form.addEventListener('submit', () => {
        ga4.event('check_eligibility_clicked', getValuesFromFormData(form), pagePath.form);
        submitSpinner.classList.remove('at-d-none');
        submitText.textContent = 'Checking eligibility';

        // Prevent disabling the fieldset before form submission
        requestAnimationFrame(() => {
          ageFieldset.disabled = true;
          studentFieldset.disabled = true;
          serviceCardFieldset.disabled = true;
          submitButton.disabled = true;

          // Ovwerride disabled button colors
          submitButton.style.background = '#444E61';
          submitButton.style.color = '#FFFFFF';
        });
      });
    }
  });
}